// Module 19 (BL-380) G6 - Hotel & travel (light): the hotel block of one event, and the shuttle flag on guests.
// The database (migration 241) is the final word; these rules mirror it so the screen can explain a problem in plain words first.
import { isRealAddress } from '../../shared/addressRules';

export const BOOKING_URL_PATTERN = /^https:\/\/[A-Za-z0-9.-]+\.[A-Za-z]{2,}(:[0-9]{1,5})?([/?#]\S*)?$/;
export const BOOKING_URL_MAX = 2000;
export const HOTEL_NAME_MAX = 120;
export const HOTEL_ADDRESS_MAX = 300;
export const RATE_NOTE_MAX = 300;
export const ARRIVAL_MAX = 200;

export const BLANK_HOTEL = Object.freeze({
    hotel_name: '', hotel_address: '', check_in: '', check_out: '', rate_note: '', booking_url: '', cutoff_date: '',
});

const text = (value) => String(value ?? '').trim();

// A database row (or nothing) -> the form's values.
// T8: a new block starts on the event's own dates, never on today.
export function hotelForm(row, event = null) {
    if (!row) return { ...BLANK_HOTEL, check_in: event?.event_date || '', check_out: event?.end_date || event?.event_date || '' };
    return {
        hotel_name: row.hotel_name || '',
        hotel_address: row.hotel_address || '',
        check_in: row.check_in || '',
        check_out: row.check_out || '',
        rate_note: row.rate_note || '',
        booking_url: row.booking_url || '',
        cutoff_date: row.cutoff_date || '',
    };
}

// The form's values -> typed columns (blank = NULL).
export function hotelRow(values) {
    const blank = (value) => (text(value) === '' ? null : text(value));
    return {
        hotel_name: text(values.hotel_name),
        hotel_address: blank(values.hotel_address),
        check_in: blank(values.check_in),
        check_out: blank(values.check_out),
        rate_note: blank(values.rate_note),
        booking_url: blank(values.booking_url),
        cutoff_date: blank(values.cutoff_date),
    };
}

const shiftDay = (day, by) => new Date(Date.parse(`${day}T00:00:00Z`) + by * 86400000).toISOString().slice(0, 10);
// T8: the date pickers stay within a week either side of the event.
export function stayRange(event) {
    if (!event?.event_date) return { min: undefined, max: undefined };
    return { min: shiftDay(event.event_date, -7), max: shiftDay(event.end_date || event.event_date, 7) };
}

export function isSafeBookingUrl(value) {
    const url = text(value);
    return url.length <= BOOKING_URL_MAX && BOOKING_URL_PATTERN.test(url);
}

// Plain-English problem, or '' when the block can be saved.
export function hotelProblem(values, event = null, savedAddress = null) {
    const name = text(values.hotel_name);
    if (!name) return 'Enter the hotel name.';
    if (name.length > HOTEL_NAME_MAX) return `Keep the hotel name to ${HOTEL_NAME_MAX} characters or fewer.`;
    // An older block may keep its address until someone changes it (the database follows the same rule).
    if (!(savedAddress && text(values.hotel_address) === text(savedAddress)) && !isRealAddress(values.hotel_address)) return 'Enter the hotel’s full address: street, city, state and ZIP, or pick it from the search.';
    if (text(values.hotel_address).length > HOTEL_ADDRESS_MAX) return `Keep the address to ${HOTEL_ADDRESS_MAX} characters or fewer.`;
    if (text(values.rate_note).length > RATE_NOTE_MAX) return `Keep the rate note to ${RATE_NOTE_MAX} characters or fewer.`;
    if (values.check_in && values.check_out && values.check_out < values.check_in) return 'Check-out cannot be before check-in.';
    const { min, max } = stayRange(event);
    if (min && ((values.check_in && (values.check_in < min || values.check_in > max)) || (values.check_out && (values.check_out < min || values.check_out > max)))) {
        return 'Check-in and check-out must be within a week of the event dates.';
    }
    const url = text(values.booking_url);
    if (url && !isSafeBookingUrl(url)) return 'The booking link must be a full secure web address starting with https://, for example https://www.hotel.com/group.';
    return '';
}

const dateLabel = (value) => {
    if (!value) return '';
    const [y, m, d] = String(value).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// "Aug 31 – Sep 2, 2027" style stay line (each date in full; simple and unambiguous).
export function stayLine(block) {
    if (!block?.check_in && !block?.check_out) return '';
    if (block.check_in && block.check_out) return `${dateLabel(block.check_in)} to ${dateLabel(block.check_out)}`;
    return block.check_in ? `Check-in ${dateLabel(block.check_in)}` : `Check-out ${dateLabel(block.check_out)}`;
}
export function cutoffLine(block) {
    return block?.cutoff_date ? `Book by ${dateLabel(block.cutoff_date)}` : '';
}

// The shuttle count on the event: every guest row flagged (a plus-one is its own seat).
export function shuttleCount(guests) {
    return (guests || []).filter((guest) => guest.needs_shuttle === true).length;
}

// Plain-English problem with arrival details, or ''.
export function arrivalProblem(value) {
    return text(value).length > ARRIVAL_MAX ? `Keep the arrival details to ${ARRIVAL_MAX} characters or fewer.` : '';
}
